import { useState } from 'react';
import { api } from '../lib/api.js';
import { kioskApi } from '../lib/kiosk-api.js';
import { INCIDENT_CATEGORY_LABELS } from '../lib/constants.js';

// Kiosk incident report — staff on shift files from the kiosk. Same
// fields as the regular form (no anonymous toggle visible since the
// reporter just PIN'd in and is already attributed). Location is the
// kiosk's. CJ feedback 2026-05-11.
export function KioskIncidentReport({ session }) {
  const locKey = session.location.key;
  function nowLocalIso() {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 16);
  }
  const [form, setForm] = useState({
    category: 'injury', severity: 'normal',
    occurred_at: nowLocalIso(),
    title: '', description: '',
    is_anonymous: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');

  async function submit() {
    if (!form.description.trim()) { setMsg('Please describe what happened.'); return; }
    setSubmitting(true); setMsg('');
    try {
      await kioskApi('POST', `/api/kiosk/${locKey}/incidents`, {
        category: form.category,
        severity: form.severity,
        occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : null,
        title: form.title.trim() || null,
        description: form.description.trim(),
        is_anonymous: !!form.is_anonymous,
      }, session.token);
      setMsg('Submitted. Managers have been notified.');
      setForm((f) => ({ ...f, title: '', description: '', is_anonymous: false, occurred_at: nowLocalIso() }));
    } catch (e) { setMsg(`Failed: ${e.message || e}`); }
    finally { setSubmitting(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="card">
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>🚨 Report an incident</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          File at <strong>{session.location?.name}</strong> as <strong>{session.user.name}</strong>.
          For life-threatening emergencies, call 911 first.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Category
            <select value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 10, fontSize: 14 }}>
              {Object.entries(INCIDENT_CATEGORY_LABELS).map(([v, l]) =>
                <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Severity
            <select value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 10, fontSize: 14 }}>
              <option value="high">High — needs attention today</option>
              <option value="normal">Normal — log for review</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            When did this happen?
            <input type="datetime-local" value={form.occurred_at}
              onChange={(e) => setForm((f) => ({ ...f, occurred_at: e.target.value }))}
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 10, fontSize: 14, boxSizing: 'border-box' }} />
          </label>
        </div>

        <label style={{ fontSize: 12, color: '#555', display: 'block', marginTop: 12, marginBottom: 4 }}>Short title (optional)</label>
        <input type="text" value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Customer slipped near front door"
          style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 12, fontSize: 15, boxSizing: 'border-box' }} />

        <label style={{ fontSize: 12, color: '#555', display: 'block', marginTop: 10, marginBottom: 4 }}>What happened? *</label>
        <textarea value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          rows={5}
          placeholder="Describe what happened, who was involved, and any immediate actions taken."
          style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 12, fontSize: 15, fontFamily: 'inherit', boxSizing: 'border-box' }} />

        <label style={{ fontSize: 14, color: '#555', display: 'flex', alignItems: 'center', gap: 10, marginTop: 12,
                          padding: '10px 14px', background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 6 }}>
          <input type="checkbox" checked={form.is_anonymous}
            onChange={(e) => setForm((f) => ({ ...f, is_anonymous: e.target.checked }))} />
          <span>Submit anonymously — your name won't be attached to this report.</span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center', marginTop: 14 }}>
          {msg && <span style={{ fontSize: 13, color: msg.startsWith('Failed') ? '#b00020' : '#2e7d32' }}>{msg}</span>}
          <button onClick={submit} disabled={submitting}
            style={{ padding: '12px 24px', border: 0, background: '#b91c1c', color: '#fff',
                      fontWeight: 700, borderRadius: 8, fontSize: 16, cursor: 'pointer' }}>
            {submitting ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}
